/**
 * VideoCallMedia: sala LiveKit com o vídeo do outro participante, a prévia da própria câmera e a
 * barra de controles (VCALL-32): microfone e câmera com menu de dispositivos, compartilhar link,
 * mais opções (tela cheia, janela flutuante, sair da sala) e encerrar. Textos em PT-BR. Mostra
 * "Reconectando…" quando a conexão de mídia cai ou quando o OUTRO lado cai (VCALL-34) e avisa
 * quando o navegador bloqueia o som. Reutilizável fora da Inbox (página do convidado).
 *
 * Três variantes (`variant`): `compact` (janela flutuante), `room` (janela ampliada) e `guest`
 * (página do convidado, com layout próprio no celular). Os menus abrem dentro do elemento da sala,
 * o mesmo que entra em tela cheia. O cronômetro fica no cabeçalho da janela; só o convidado, que
 * não tem cabeçalho, o vê aqui, no canto de cima. A sala pode ir para uma janela flutuante FORA da
 * guia (Document Picture-in-Picture, ver `room/popoutWindow`): a conexão continua aqui e o React
 * desenha a mesma sala na outra janela, sempre compacta; na guia fica só um aviso. A barra também compartilha a tela
 * (a tela apresentada, a do outro lado ou a própria, ocupa o palco, com a câmera do outro lado em miniatura) e o menu da câmera troca o fundo (desfoque ou imagem):
 * o estado dos dois vive acima da troca de janela (`ScreenShareProvider`, `BackgroundEffectProvider`; ver
 * docs/specs/chamada-video-tela-fundo). Numa chamada com a Jev
 * (`transcript`), a sala ganha legenda ao vivo, o marcador de momentos e, ampliada, o painel da chamada com a
 * transcrição por pessoa; o estado dela vive acima da troca de janela (`TranscriptProvider`) para não se perder.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    isTrackReference, LiveKitRoom, RoomAudioRenderer, useConnectionState, useIsMuted, useIsSpeaking, useTracks, VideoTrack,
} from '@livekit/components-react';
import { ConnectionState, Track } from 'livekit-client';
import { Loader2, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDraggablePreview } from './useDraggablePreview';
import { AudioBlockedNotice } from './room/AudioBlockedNotice';
import { InviteCard, useInvitePhase } from './room/InviteCard';
import { RemoteTile, TileView } from './room/ParticipantTile';
import { PeerLeftTracker } from './room/usePeerLeftOnPurpose';
import { CallPanel, TRANSCRIPT_TAB, panelIdOf, tabIdOf } from './room/CallPanel';
import { ParticipantsPanel } from './room/ParticipantsPanel';
import { PoppedOutNotice } from './room/PoppedOutNotice';
import { MeetingCountdown } from './room/MeetingCountdown';
import { RoomControlBar } from './room/RoomControlBar';
import { captureOptionsFrom, devicePreferenceStore } from './room/devicePreference';
import { RoomPortalProvider } from './room/roomPortal';
import { BackgroundEffectProvider } from './room/BackgroundEffectProvider';
import { ScreenShareProvider } from './room/ScreenShareProvider';
import { RoomStatusStack } from './room/RoomStatusStack';
import { SharedScreen } from './room/ScreenShareView';
import { useNarrowScreen } from './room/useNarrowScreen';
import { usePopoutWindow } from './room/usePopoutWindow';
import { initialsOf, displayNameOf, toneFor } from './room/participantDisplay';
import { LiveCaptions } from './transcript/LiveCaptions';
import { MomentNotice } from './transcript/MomentNotice';
import { RecordingIndicator } from './transcript/RecordingIndicator';
import { GuestRecordingConsent } from './recording/GuestRecordingConsent';
import { RecordingProvider } from './recording/RecordingProvider';
import { RoomRecordingBadge } from './recording/RoomRecordingBadge';
import { TranscriptPanel } from './transcript/TranscriptPanel';
import { TranscriptProvider } from './transcript/TranscriptProvider';
import { isPanelOpen } from './transcript/panelState';
import { useCallTranscript } from './transcript/transcriptContext';
import { isTranscriberIdentity } from './transcript/transcriptProtocol';

/** Cronômetro "mm:ss" (ou "h:mm:ss"). */
export function formatElapsed(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${pad(m)}:${pad(s % 60)}`;
}

export const DEFAULT_WAITING_LABEL = 'Aguardando o contato entrar';
export const DEFAULT_RECONNECT_LABEL = 'Aguardando o contato reconectar';
export const DEFAULT_LEFT_LABEL = 'O contato saiu da chamada';
export const DEFAULT_LEFT_HINT = 'O link continua valendo: o contato pode voltar a qualquer momento.';

const RECONNECTING = new Set([ConnectionState.Reconnecting, ConnectionState.SignalReconnecting]);

const CAMERA_WITH_PLACEHOLDER = [{ source: Track.Source.Camera, withPlaceholder: true }];
const SCREEN_SHARE = [{ source: Track.Source.ScreenShare, withPlaceholder: false }];

/** Tamanho da prévia da própria câmera e do avatar do outro participante em cada variante. */
const PREVIEW_CLASS = Object.freeze({
    compact: 'w-24',
    // Em tela estreita a sala ampliada divide a altura com o painel da chamada: a prévia é pequena como na compacta.
    room: 'w-24 min-[1100px]:w-[256px]',
    guest: 'w-[96px] sm:w-[200px]',
});
const REMOTE_SIZE = Object.freeze({ compact: 'md', room: 'lg', guest: 'lg' });
/** No celular do convidado a prévia começa no canto de cima, longe da barra que flutua sobre o vídeo. */
const PREVIEW_START = Object.freeze({ guest: { x: 1, y: 0 } });

/** No celular do convidado a barra flutua sobre o vídeo: o nome sobe e o aviso desce para não ficarem por baixo. */
const LABEL_CLASS = Object.freeze({ guest: 'bottom-[112px] sm:bottom-3' });
const NOTICE_CLASS = Object.freeze({ guest: 'top-[152px] sm:top-3' });
/** Com a tela do outro lado no palco, a câmera dele é uma miniatura no canto de baixo (acima da barra no celular do convidado). */
const THUMB_POSITION = Object.freeze({
    compact: 'bottom-2 left-2',
    room: 'bottom-3 left-3',
    guest: 'bottom-[112px] left-3 sm:bottom-3',
});

const STAGE_CLASS = Object.freeze({
    compact: 'relative min-h-0 flex-1 bg-black',
    // Sem `overflow-hidden`: o cartão do convite precisa sair do palco até o botão de compartilhar, na barra.
    // O vídeo continua com os cantos redondos porque o tile herda o raio do palco.
    room: 'relative min-h-0 flex-1 rounded-2xl bg-black',
    guest: 'absolute inset-0 bg-black sm:relative sm:inset-auto sm:min-h-0 sm:flex-1',
});

/** Sala ampliada larga (esboço 3.3): o vídeo é um cartão afastado da borda, com o mesmo espaço do painel ao lado. */
const STAGE_INSET = 'mx-3 mt-3';

/** Prévia da própria câmera; arrastável para qualquer ponto do palco. */
function LocalPreview({ trackRef, stageRef, variant }) {
    const { style, dragging, handlers } = useDraggablePreview(stageRef, PREVIEW_START[variant]);
    const { participant } = trackRef;
    const name = displayNameOf(participant, 'Você');
    // Câmera desligada: a trilha segue publicada, só silenciada (quadro preto): mostra a inicial.
    const cameraMuted = useIsMuted({ participant, source: Track.Source.Camera });
    const speaking = useIsSpeaking(participant);
    return (
        <div
            role="group"
            tabIndex={0}
            aria-label="Sua câmera (arraste ou use as setas para mover)"
            title="Arraste para mover"
            data-local-preview=""
            style={style}
            className={cn(
                'absolute z-10 touch-none select-none rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
                PREVIEW_CLASS[variant],
                dragging ? 'cursor-grabbing' : 'cursor-grab',
            )}
            {...handlers}
        >
            <TileView
                name="Você"
                initials={initialsOf(name)}
                tone={toneFor(participant.identity ?? name)}
                cameraOn={isTrackReference(trackRef) && !cameraMuted}
                speaking={speaking}
                size="xs"
                label={variant !== 'compact'}
                className={cn('pointer-events-none w-full rounded-xl border border-white/20 shadow-lg', variant === 'guest' ? 'aspect-[3/4] sm:aspect-video' : 'aspect-video')}
            >
                {/* Espelhada como num espelho (igual Meet/WhatsApp): sem isso, inclinar para a esquerda
                    aparece como para a direita. Só a exibição local: o outro lado recebe a imagem real. */}
                <VideoTrack trackRef={trackRef} muted data-local-video="" className="absolute inset-0 h-full w-full -scale-x-100 object-cover" />
            </TileView>
        </div>
    );
}

/** O outro lado clicou em sair: não é queda, então sem spinner nem "reconectando". */
function PeerLeftNotice({ label, hint }) {
    return (
        <div role="status" className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/80">
                <LogOut className="h-5 w-5" aria-hidden="true" />
            </span>
            <p className="text-sm font-medium text-white">{label}</p>
            {hint && <p className="max-w-xs text-xs text-white/60">{hint}</p>}
        </div>
    );
}

function Stage({ stageRef, variant, inset = false, remote, share, local, waitingLabel, leftNotice, overlay, children }) {
    return (
        <div ref={stageRef} className={cn(STAGE_CLASS[variant], inset && STAGE_INSET)}>
            {share ? (
                <SharedScreen trackRef={share} camera={remote} thumbClassName={PREVIEW_CLASS[variant]} thumbPosition={THUMB_POSITION[variant]} />
            ) : remote ? (
                <RemoteTile trackRef={remote} size={REMOTE_SIZE[variant]} labelClassName={LABEL_CLASS[variant]} />
            ) : leftNotice ? (
                <PeerLeftNotice {...leftNotice} />
            ) : (
                waitingLabel && (
                    <div role="status" className="flex h-full items-center justify-center gap-2 text-sm text-white/70">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {waitingLabel}
                    </div>
                )
            )}
            {children}
            {overlay}
            <AudioBlockedNotice className={NOTICE_CLASS[variant]} />
            {local && <LocalPreview trackRef={local} stageRef={stageRef} variant={variant} />}
        </div>
    );
}

function ReconnectingBanner() {
    return (
        <div role="status" className="absolute inset-x-0 top-0 z-20 bg-amber-500/90 py-1 text-center text-xs font-medium text-black">
            Reconectando…
        </div>
    );
}

/** O outro participante já esteve na sala e saiu (caiu ou foi embora): a sala vai tentar de novo. */
function usePeerGone(present) {
    const [hadPeer, setHadPeer] = useState(false);
    useEffect(() => {
        if (present) setHadPeer(true);
    }, [present]);
    return hadPeer && !present;
}

/**
 * Conteúdo da sala (dentro do LiveKitRoom): palco, aviso de reconexão, cronômetro (do convidado, ou
 * de quem está na janela flutuante, que não tem o cabeçalho) e a barra. O aviso vale para a conexão de mídia DESTE lado e para a queda do outro lado: o atendente
 * também precisa ver "Reconectando…" quando é o convidado que cai (e vice-versa).
 */
function RoomContent({
    stageRef, root, variant, popped, popout, elapsedLabel, hangUpLabel, onHangUp, onLeaveRoom, onCopyLink, invite,
    peerReconnecting, peerLeft, waitingLabel, reconnectLabel, leftLabel, leftHint, transcript, participants, children,
}) {
    // O transcritor da Jev (`tx:`) está na sala mas não é uma pessoa: nunca vira o "outro participante".
    const tracks = useTracks(CAMERA_WITH_PLACEHOLDER).filter((t) => !isTranscriberIdentity(t.participant.identity));
    const remote = tracks.find((t) => !t.participant.isLocal);
    const local = tracks.find((t) => t.participant.isLocal);
    // A tela no palco: a que o outro lado apresenta e, se for a minha, a minha também, como no Google Meet (quem compartilha a
    // tela toda vê o espelho infinito, e é o esperado). Se os dois apresentam, a do outro lado vence; os avisos de quem apresenta
    // ficam na `RoomStatusStack`.
    const shares = useTracks(SCREEN_SHARE).filter((t) => !isTranscriberIdentity(t.participant.identity));
    const remoteShare = shares.find((t) => !t.participant.isLocal);
    const stageShare = remoteShare ?? shares.find((t) => t.participant.isLocal);
    const peerGone = usePeerGone(Boolean(remote));
    // Saiu de propósito (clicou em sair): não é queda, então não há "Reconectando…" (nem o que o servidor
    // diz de IDLE, que vale igual para quem caiu e para quem saiu).
    const peerSaid = peerLeft && !remote;
    const peerDropped = !peerSaid && (peerGone || peerReconnecting);
    const ownConnectionDropped = RECONNECTING.has(useConnectionState());
    // Até o primeiro convidado entrar, o título e o link ficam na tela; depois o cartão voa para o botão.
    const shareRef = useRef(null);
    const [invitePhase, inviteGone] = useInvitePhase(Boolean(invite?.awaiting) && !remote);
    const showInvite = Boolean(invite) && invitePhase !== 'gone';
    const transcriptState = useCallTranscript();
    // A transcrição (o painel da chamada) existe em todas as janelas do atendente. A sala ampliada larga a mostra ao lado
    // do vídeo (e começa com ela aberta); em tela estreita não há largura para isso: vira uma folha entre o vídeo e a
    // barra, e a barra usa o tamanho compacto. A janelinha da guia e a janela flutuante (sempre compactas) a abrem como
    // uma gaveta de altura fixa embaixo do vídeo, pelo botão da barra, e a janela cresce para acomodá-la.
    const narrowRoom = useNarrowScreen() && variant === 'room' && Boolean(transcript);
    const barVariant = narrowRoom ? 'compact' : variant;
    const panelLayout = variant === 'room' ? (narrowRoom ? 'sheet' : 'side') : 'dock';
    const hasPanel = Boolean(transcript) && variant !== 'guest';
    const panelVisible = hasPanel && isPanelOpen(transcriptState?.panelPref, variant);
    const timerShown = variant === 'guest' || popped;
    const sideLayout = panelLayout === 'side';
    // Com a transcrição e a sala larga, a barra ocupa a largura toda, por baixo do vídeo E do painel (esboço 3.3): o vídeo
    // e o painel são dois cartões de mesma altura lado a lado. Nos outros casos a barra fica na coluna do vídeo.
    const barBelowBoth = Boolean(transcript) && sideLayout;
    // Abas do painel: a Transcrição e, para o atendente, os Participantes (quem espera para entrar e quem está na chamada). O selo
    // da aba conta quem espera, para o atendente notar mesmo olhando a transcrição.
    const [panelTab, setPanelTab] = useState(TRANSCRIPT_TAB.id);
    const waitingCount = participants?.waiting?.length ?? 0;
    const panelTabs = participants
        ? [TRANSCRIPT_TAB, { id: 'participants', label: 'Participantes', badge: waitingCount }]
        : [TRANSCRIPT_TAB];
    const activePanelTab = panelTabs.some((tab) => tab.id === panelTab) ? panelTab : TRANSCRIPT_TAB.id;
    const panel = panelVisible && (
        <CallPanel layout={panelLayout} tabs={panelTabs} activeTab={activePanelTab} onTabChange={setPanelTab} ownPanels onClose={() => transcriptState.setPanelPref(false)}>
            {/* A Transcrição fica montada (só escondida) para não perder a rolagem ao trocar de aba */}
            <div
                role="tabpanel"
                id={panelIdOf(TRANSCRIPT_TAB.id)}
                aria-labelledby={tabIdOf(TRANSCRIPT_TAB.id)}
                className={activePanelTab === TRANSCRIPT_TAB.id ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}
            >
                <TranscriptPanel title={transcript.title} compact={panelLayout !== 'side'} />
            </div>
            {participants && activePanelTab === 'participants' && (
                <div role="tabpanel" id={panelIdOf('participants')} aria-labelledby={tabIdOf('participants')} className="flex min-h-0 flex-1 flex-col">
                    <ParticipantsPanel waiting={participants.waiting} onAdmit={participants.onAdmit} onReject={participants.onReject} busy={participants.busy} />
                </div>
            )}
        </CallPanel>
    );
    const controlBar = (
        <RoomControlBar
            variant={barVariant}
            panelToggle={hasPanel}
            panelToggleInline={!sideLayout}
            panelVariant={variant}
            elapsedLabel={elapsedLabel}
            hangUpLabel={hangUpLabel}
            onHangUp={onHangUp}
            onLeaveRoom={onLeaveRoom}
            onCopyLink={onCopyLink}
            root={root}
            shareRef={shareRef}
            popout={popout}
        />
    );
    const content = (
        <>
            <Stage
                stageRef={stageRef}
                variant={variant}
                inset={variant === 'room' && sideLayout}
                remote={remote}
                share={stageShare}
                local={local}
                waitingLabel={peerDropped ? reconnectLabel : (!showInvite && waitingLabel)}
                leftNotice={peerSaid ? { label: leftLabel, hint: leftHint } : null}
                overlay={(
                    <>
                        <RoomStatusStack presenter={remoteShare?.participant} compact={variant === 'compact'} className={NOTICE_CLASS[variant]} />
                        {transcript && <LiveCaptions variant={variant} />}
                        {transcript && <MomentNotice />}
                        {/* Cronômetro (guest e janela flutuante, que não têm cabeçalho) e o ícone de gravação, juntos no canto. */}
                        <div
                            className={cn(
                                'pointer-events-none absolute z-10 flex items-center gap-2',
                                popped ? 'left-2 top-2' : timerShown ? 'left-4 top-5' : 'left-3 top-3',
                            )}
                        >
                            {timerShown && (
                                <span
                                    aria-label="Duração da chamada"
                                    className={cn('font-mono text-xs tabular-nums text-neutral-200', popped && 'rounded-md bg-black/50 px-1.5 py-0.5')}
                                >
                                    {elapsedLabel}
                                </span>
                            )}
                            <RecordingIndicator />
                            {/* A gravação da chamada (diferente da transcrição): "Gravando", ou o pedido esperando o aceite. Todos veem. */}
                            <RoomRecordingBadge compact={variant === 'compact' || popped} />
                            {/* Faltando 5 minutos ou menos para o fim da duração máxima do plano: contagem até a chamada acabar */}
                            <MeetingCountdown compact={variant === 'compact' || popped} />
                        </div>
                    </>
                )}
            >
                {showInvite && (
                    <InviteCard
                        invite={invite}
                        variant={variant}
                        phase={invitePhase}
                        onCopy={onCopyLink}
                        onGone={inviteGone}
                        targetRef={shareRef}
                    />
                )}
            </Stage>
            {(ownConnectionDropped || peerDropped) && <ReconnectingBanner />}
            {/* O aceite de ser gravado (só aparece para o convidado, quando a sala pede ou já grava) */}
            <GuestRecordingConsent />
            {!sideLayout && panel}
            {children}
            {!barBelowBoth && controlBar}
        </>
    );
    // Com a transcrição, a coluna do vídeo e o painel ficam lado a lado numa estrutura que NÃO muda ao abrir ou fechar
    // o painel (senão o vídeo remontaria e a prévia perderia a posição).
    if (!transcript) return content;
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1">
                <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">{content}</div>
                {sideLayout && panelVisible && <div className="flex shrink-0 pr-3 pt-3">{panel}</div>}
            </div>
            {barBelowBoth && controlBar}
        </div>
    );
}

/**
 * @param {{
 *   connection: { token: string, wsUrl: string },
 *   elapsedSeconds?: number,
 *   onHangUp: () => void,
 *   hangUpLabel?: string,
 *   onLeaveRoom?: () => void,
 *   onCopyLink?: (doc: Document) => unknown,
 *   invite?: { title: string, url: string, contactName?: string, awaiting: boolean },
 *   popout?: ReturnType<typeof usePopoutWindow>,
 *   variant?: 'compact' | 'room' | 'guest',
 *   waitingLabel?: string | null,
 *   reconnectLabel?: string | null,
 *   peerReconnecting?: boolean,
 *   leftLabel?: string,
 *   leftHint?: string | null,
 *   onPeerLeftChange?: (left: boolean) => void,
 *   onDisconnected?: () => void,
 *   transcript?: { videoCallId: string, originAt?: string|null, title?: string, active?: boolean, api: object, panelPref?: boolean|null, onPanelPrefChange?: (open: boolean) => void },
 *   recording?: { agent?: { videoCallId: string, api: object }, guest?: { token: string, acceptedAtJoin?: boolean, api: object, onLeave?: () => void } },
 *   className?: string,
 *   children?: React.ReactNode,
 * }} props: `onLeaveRoom` (salas por link) acrescenta "Sair da sala" em "Mais opções". `onCopyLink`
 *   (salas por link) liga o botão de compartilhar da barra. `invite` mostra o título e o link na tela
 *   enquanto `awaiting` (ninguém entrou ainda); quando o primeiro convidado entra, o cartão voa até o
 *   botão de compartilhar. `popout` é o controle da janela flutuante quando quem monta a sala precisa dele
 *   (o botão no cabeçalho da janela do atendente); sem ele a sala cria o seu. `elapsedSeconds` só aparece na variante `guest` (nas janelas o cronômetro é do
 *   cabeçalho; na janela flutuante, que não tem cabeçalho, aparece sempre). `waitingLabel` é o texto da
 *   sala sem o outro participante e `reconnectLabel` o de quando ele caiu (`null` para não mostrar
 *   nenhum). `peerReconnecting` força o aviso
 *   "Reconectando…" (o servidor já sabe que o outro lado caiu). `leftLabel` e `leftHint` são o texto de quando o
 *   outro lado SAIU DE PROPÓSITO (`onPeerLeftChange` avisa quem monta a sala, para o cabeçalho dizer o mesmo). `children` renderiza dentro da sala
 *   (pode usar os hooks do LiveKit). `transcript` liga a Jev na sala do atendente (legenda, momentos e painel);
 *   `transcript.active`: a chamada está em andamento (a Jev deve estar na sala). O ícone de gravação aparece para
 *   todos (atendente e convidado) enquanto o transcritor está na sala. `recording` liga a GRAVAÇÃO da chamada (docs/specs/otto/
 *   RECORDING.md): `agent` na sala de quem criou a chamada (botão de gravar e a cota), `guest` na página do convidado (o aceite);
 *   o aviso "Gravando" aparece para todos, vindo do metadado da sala.
 */
export function VideoCallMedia({
    connection, elapsedSeconds = 0, onHangUp, hangUpLabel = 'Encerrar', onLeaveRoom, onCopyLink, invite, popout: popoutFromParent,
    variant = 'room', waitingLabel = DEFAULT_WAITING_LABEL, reconnectLabel = DEFAULT_RECONNECT_LABEL, peerReconnecting = false,
    leftLabel = DEFAULT_LEFT_LABEL, leftHint = DEFAULT_LEFT_HINT, onPeerLeftChange,
    onDisconnected, transcript, participants, recording, className, children,
}) {
    const stageRef = useRef(null);
    const [root, setRoot] = useState(null);
    // Câmera, microfone e alto-falante preferidos da pessoa (os que ela escolheu antes): lidos UMA vez ao entrar. O LiveKit
    // recria a sala quando as opções mudam, então escolher outro dispositivo durante a chamada não pode refazer isto.
    const [capture] = useState(() => captureOptionsFrom(devicePreferenceStore.read()));
    // A janela flutuante leva a sala para fora da guia: a mesma árvore, desenhada por um portal na
    // outra janela, sempre no tamanho compacto. Na guia fica só o aviso.
    const ownPopout = usePopoutWindow();
    const popout = popoutFromParent ?? ownPopout;
    // A sala sai da tela (a chamada acabou): a janela flutuante fecha junto, também quando o controle é do pai.
    const { close: closePopout } = popout;
    useEffect(() => closePopout, [closePopout]);
    const popped = Boolean(popout.container);
    // O outro lado saiu de propósito? Guardado aqui (e não na sala desenhada), porque a sala é recriada ao ir para
    // a janela flutuante e voltar, e não pode esquecer.
    const [peerLeft, setPeerLeft] = useState(false);
    useEffect(() => { onPeerLeftChange?.(peerLeft); }, [peerLeft, onPeerLeftChange]);
    useEffect(() => () => onPeerLeftChange?.(false), [onPeerLeftChange]);
    const room = (
        <div ref={setRoot} className="dark relative flex min-h-0 flex-1 flex-col bg-black text-neutral-50">
            <RoomContent
                stageRef={stageRef}
                root={root}
                variant={popped ? 'compact' : variant}
                popped={popped}
                popout={popout}
                elapsedLabel={formatElapsed(elapsedSeconds)}
                hangUpLabel={hangUpLabel}
                onHangUp={onHangUp}
                onLeaveRoom={onLeaveRoom}
                onCopyLink={onCopyLink}
                invite={invite}
                peerReconnecting={peerReconnecting}
                peerLeft={peerLeft}
                waitingLabel={waitingLabel}
                reconnectLabel={reconnectLabel}
                leftLabel={leftLabel}
                leftHint={leftHint}
                transcript={transcript}
                participants={participants}
            >
                {children}
            </RoomContent>
        </div>
    );
    const inRoomContent = (
        <>
            <PeerLeftTracker onChange={setPeerLeft} />
            <RoomPortalProvider value={root}>
                {popped ? (
                    <>
                        {createPortal(room, popout.container)}
                        <PoppedOutNotice onBringBack={popout.close} />
                    </>
                ) : room}
            </RoomPortalProvider>
            <RoomAudioRenderer />
        </>
    );
    // A gravação fica DENTRO da sala (lê o metadado do LiveKit) e acima de onde ela é recriada (janela flutuante)
    const inRoom = recording ? <RecordingProvider agent={recording.agent} guest={recording.guest}>{inRoomContent}</RecordingProvider> : inRoomContent;
    return (
        <LiveKitRoom
            serverUrl={connection.wsUrl}
            token={connection.token}
            connect
            audio={capture.audio}
            video={capture.video}
            options={capture.roomOptions}
            onDisconnected={onDisconnected}
            className={cn('flex min-h-0 flex-1 flex-col', className)}
        >
            <ScreenShareProvider>
                <BackgroundEffectProvider>
                    {transcript ? (
                        <TranscriptProvider
                            videoCallId={transcript.videoCallId}
                            originAt={transcript.originAt ?? null}
                            active={transcript.active ?? true}
                            api={transcript.api}
                            panelPref={transcript.panelPref}
                            onPanelPrefChange={transcript.onPanelPrefChange}
                        >
                            {inRoom}
                        </TranscriptProvider>
                    ) : inRoom}
                </BackgroundEffectProvider>
            </ScreenShareProvider>
        </LiveKitRoom>
    );
}

export default VideoCallMedia;
